<template>
  <div class="ss-wrap" ref="wrap">
    <div
      class="ss-input-row"
      :class="{
        'ss-input-row--focus': open,
        'ss-input-row--disabled': disabled,
        'ss-input-row--selected': !open && modelValue !== null && modelValue !== '' && modelValue !== undefined
      }"
      @click="onBoxClick"
    >
      <input
        ref="input"
        v-model="query"
        class="ss-input"
        :placeholder="selectedLabel || placeholder"
        :disabled="disabled"
        autocomplete="off"
        @focus="open = true"
        @input="open = true"
        @keydown.escape="close"
        @keydown.enter.prevent="pickFirst"
        @keydown.tab="close"
      />
      <button v-if="modelValue !== null && modelValue !== '' && modelValue !== undefined" class="ss-clear" @mousedown.prevent="onClear" tabindex="-1">×</button>
      <svg v-else class="ss-caret" viewBox="0 0 12 8" fill="none"><path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
    </div>

    <teleport to="body">
      <ul
        v-if="open && filtered.length > 0"
        class="ss-drop"
        :style="dropStyle"
      >
        <li
          v-for="opt in filtered"
          :key="opt._key"
          class="ss-opt"
          :class="{ 'ss-opt--active': isSelected(opt) }"
          @mousedown.prevent="pick(opt)"
        >{{ opt._label }}</li>
      </ul>
      <div
        v-if="open && filtered.length === 0"
        class="ss-drop ss-drop--empty"
        :style="dropStyle"
      >No results for "{{ query }}"</div>
    </teleport>
  </div>
</template>

<script>
export default {
  name: 'SearchableSelect',
  props: {
    modelValue:  { default: null },
    options:     { type: Array, default: () => [] },
    labelKey:    { type: String, default: 'name' },
    valueKey:    { type: String, default: null },   // null = whole object
    placeholder: { type: String, default: '--Select--' },
    disabled:    { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return { query: '', open: false, dropStyle: {} }
  },
  computed: {
    normalized() {
      return (this.options || []).map((o, i) => {
        const label = typeof o === 'string' ? o : (o[this.labelKey] != null ? String(o[this.labelKey]) : (o.Name ?? o.name ?? o.label ?? String(o)))
        const value = this.valueKey ? o[this.valueKey] : o
        return { _key: i, _label: label, _value: value, _raw: o }
      })
    },
    filtered() {
      const q = this.query.trim().toLowerCase()
      if (!q) return this.normalized
      return this.normalized.filter(o => o._label.toLowerCase().includes(q))
    },
    selectedLabel() {
      if (this.modelValue === null || this.modelValue === '' || this.modelValue === undefined) return ''
      const match = this.normalized.find(o => {
        if (this.valueKey) return o._value === this.modelValue
        return JSON.stringify(o._value) === JSON.stringify(this.modelValue)
      })
      return match ? match._label : ''
    },
  },
  watch: {
    open(val) {
      if (val) {
        this.$nextTick(() => this.calcDrop())
      }
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onOutside)
  },
  unmounted() {
    document.removeEventListener('mousedown', this.onOutside)
  },
  methods: {
    onBoxClick() {
      if (this.disabled) return
      this.open = !this.open
      if (this.open) this.$nextTick(() => this.$refs.input?.focus())
    },
    onOutside(e) {
      if (this.$refs.wrap && !this.$refs.wrap.contains(e.target)) this.close()
    },
    close() {
      this.open = false
      this.query = ''
    },
    pick(opt) {
      this.$emit('update:modelValue', opt._value)
      this.$emit('change', opt._value)
      this.close()
    },
    pickFirst() {
      if (this.filtered.length) this.pick(this.filtered[0])
    },
    onClear() {
      this.$emit('update:modelValue', null)
      this.$emit('change', null)
      this.query = ''
      this.open = false
    },
    isSelected(opt) {
      if (this.modelValue === null || this.modelValue === undefined) return false
      if (this.valueKey) return opt._value === this.modelValue
      return JSON.stringify(opt._value) === JSON.stringify(this.modelValue)
    },
    calcDrop() {
      const el = this.$refs.wrap
      if (!el) return
      const r = el.getBoundingClientRect()
      const spaceBelow = window.innerHeight - r.bottom
      const maxH = Math.min(240, spaceBelow > 120 ? spaceBelow - 12 : r.top - 12)
      this.dropStyle = {
        position: 'fixed',
        top: (spaceBelow > 120 ? r.bottom + 4 : r.top - maxH - 4) + 'px',
        left: r.left + 'px',
        width: r.width + 'px',
        maxHeight: maxH + 'px',
        zIndex: 99999,
      }
    },
  },
}
</script>

<style>
.ss-wrap { position: relative; width: 100%; font-family: 'Inter', system-ui, sans-serif; }

.ss-input-row {
  display: flex; align-items: center; gap: 6px;
  width: 100%; min-height: 38px;
  border: 1.5px solid #e2e8f0; border-radius: 8px;
  padding: 0 10px; background: #fff; cursor: text;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.ss-input-row--focus    { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.10); }
.ss-input-row--disabled { background: #f8fafc; cursor: not-allowed; opacity: 0.6; }
.ss-input-row--selected { background: #f1f5f9; border-color: #cbd5e1; }
.ss-input-row--selected .ss-input::placeholder { color: #374151; font-weight: 500; }
.ss-input-row--selected .ss-caret { color: #64748b; }

.ss-input {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 13.5px; font-family: inherit; color: #0f172a;
  min-width: 0; cursor: text;
}
.ss-input::placeholder { color: #94a3b8; }
.ss-input:disabled { cursor: not-allowed; }

.ss-clear {
  background: none; border: none; color: #94a3b8; font-size: 17px;
  line-height: 1; cursor: pointer; padding: 0 2px; flex-shrink: 0;
}
.ss-clear:hover { color: #374151; }
.ss-caret { width: 10px; height: 8px; color: #94a3b8; flex-shrink: 0; pointer-events: none; }

.ss-drop {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12); list-style: none;
  overflow-y: auto; padding: 4px 0; margin: 0;
  scrollbar-width: thin; scrollbar-color: #e2e8f0 transparent;
}
.ss-drop::-webkit-scrollbar { width: 4px; }
.ss-drop::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
.ss-drop--empty { padding: 12px 16px; font-size: 13px; color: #94a3b8; }

.ss-opt {
  padding: 9px 14px; font-size: 13.5px; color: #374151;
  cursor: pointer; transition: background 0.1s;
}
.ss-opt:hover      { background: #f1f5f9; }
.ss-opt--active    { background: #ede9fe; color: #4f46e5; font-weight: 600; }
</style>
